import React, { useEffect } from "react";
import { View, ScrollView } from "react-native";
import {
  CommonActions,
  type NavigationProp,
  type ParamListBase,
  useNavigation,
} from "@react-navigation/native";
import { Text } from "@louise/ui/Text/Text";
import {
  ArrowRight,
  Check,
  ClipboardCheck,
  RotateCcw,
  Sparkles,
} from "lucide-react-native";

import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import {
  ActionBanner,
  CloseButton,
  haptic,
  PrimaryButton,
} from "@louise/moon-ui";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import type { Patient } from "@src/packages/patients/patients.types";
import { getNextSection } from "../getNextIncompleteSection";
import { useMedicalHistoryQuestionMetadata } from "./MedicalHistoryQuestionContext";
import { usePatientSyncStatus } from "@src/packages/patients/sync/usePatientSync";

export interface BaseCompleteScreenProps {
  /** Main title text. */
  title?: string;
  /** Small context label shown above the title. */
  sectionLabel?: string;
  /** Whether to show the optional context label. Defaults to true. */
  showSectionLabel?: boolean;
  /** Primary subtitle text */
  subtitle: string;
  /** Secondary subtitle text (optional, pass null to hide) */
  secondarySubtitle?: string | null;
  /** Short recap of the answers captured in this section. */
  recap?: string | null;
  /** Whether to show the recap label. Defaults to true. */
  showRecapLabel?: boolean;
  /** Personalized explanation of why the captured answers matter. */
  personalizedMessage?: string | null;
  /** Whether to show the insight label and sparkle icon. Defaults to true. */
  showInsightLabel?: boolean;
  /** Button label when not submitting */
  buttonLabel?: string;
  /** Button label when submitting */
  submittingLabel?: string;
  /** Whether submission is in progress */
  isSubmitting: boolean;
  /** Called when button is pressed */
  onFinish: () => void;
  /**
   * Why the last save didn't work. Shown above the button with Try again,
   * which runs the same save.
   */
  saveError?: string | null;
  /** Optional override for the close affordance. */
  onClose?: () => void;
  /** Optional custom content to render above the button */
  children?: React.ReactNode;
  /** Optional illustration component to render above the title */
  illustration?: React.ReactNode;
}

/**
 * Shared complete screen layout for section transitions.
 *
 * This is intentionally a full-screen surface. Completion is a step in the
 * questionnaire, not a modal dialog, so the background and navigation remain
 * part of the experience instead of being trapped inside another card.
 */
export function BaseCompleteScreen({
  title,
  sectionLabel,
  showSectionLabel = true,
  subtitle,
  secondarySubtitle,
  recap,
  showRecapLabel = true,
  personalizedMessage,
  showInsightLabel = true,
  buttonLabel,
  submittingLabel,
  isSubmitting,
  onFinish,
  saveError,
  onClose,
  children,
  illustration,
}: BaseCompleteScreenProps) {
  const t = useTranslations();
  const navigation = useNavigation<NavigationProp<ParamListBase>>();
  const questionMetadata = useMedicalHistoryQuestionMetadata();
  const sync = usePatientSyncStatus();
  const hasLocalAnswers =
    !sync.hydrated ||
    sync.inFlight ||
    sync.pending > 0 ||
    sync.failed.length > 0 ||
    sync.accepting > 0 ||
    sync.undurable > 0;
  const resolvedTitle = title ?? t("med_history.complete.title_default");
  const resolvedSecondarySubtitle =
    secondarySubtitle === undefined
      ? t("med_history.complete.secondary_subtitle_default")
      : secondarySubtitle;
  const resolvedButtonLabel =
    buttonLabel ?? t("med_history.complete.button_label_default");
  const resolvedSubmittingLabel =
    submittingLabel ?? t("med_history.complete.submitting_label_default");
  const shouldShowSectionLabel = Boolean(sectionLabel && showSectionLabel);

  const sectionCompleted = questionMetadata?.sectionCompleted;

  useEffect(() => {
    haptic("success");
    // Reaching this screen *is* the section being finished — the button below
    // only decides where the patient goes next, so waiting for it would lose
    // everyone who closes on the recap.
    sectionCompleted?.();
  }, [sectionCompleted]);

  const handleClose = () => {
    haptic("selection");
    const close = onClose ?? questionMetadata?.onClose;
    if (close) {
      close();
      return;
    }

    const parent = navigation.getParent();
    if (parent?.canGoBack()) {
      parent.goBack();
    } else if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate("MedicalHistory");
    }
  };

  return (
    <GlassScreenLayout>
      <View style={{ flex: 1 }}>
        <View
          style={{
            width: "100%",
            maxWidth: 520,
            alignSelf: "center",
            alignItems: "flex-end",
            paddingHorizontal: 20,
            paddingTop: 12,
          }}
        >
          <CloseButton
            testID="medical-history-complete-close"
            accessibilityLabel={t("learn.article_actions.close_accessibility")}
            action="close_med_history_complete"
            onPress={handleClose}
          />
        </View>

        <ScrollView
          contentContainerStyle={{
            flexGrow: 1,
            paddingHorizontal: 24,
            paddingBottom: 28,
          }}
          showsVerticalScrollIndicator={false}
        >
          <View
            style={{
              width: "100%",
              maxWidth: 520,
              alignSelf: "center",
              flexGrow: 1,
              justifyContent: "center",
              alignItems: "center",
              paddingVertical: 24,
            }}
          >
            {illustration ?? <CompletionIllustration />}

            {shouldShowSectionLabel ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  fontWeight: "500",
                  color: EMBER.colors.textLight,
                  textAlign: "center",
                  marginTop: 18,
                  marginBottom: 8,
                }}
              >
                {sectionLabel}
              </Text>
            ) : null}

            <Text
              className="font-dm-sans"
              style={{
                fontSize: 28,
                fontWeight: "700",
                lineHeight: 34,
                color: EMBER.colors.text,
                textAlign: "center",
                marginTop: shouldShowSectionLabel ? 0 : 18,
                marginBottom: 10,
              }}
            >
              {resolvedTitle}
            </Text>

            <Text
              className="font-dm-sans"
              style={{
                fontSize: 15,
                fontWeight: "400",
                color: EMBER.colors.textLight,
                textAlign: "center",
                lineHeight: 22,
                maxWidth: 460,
                marginBottom: recap || personalizedMessage ? 22 : 8,
              }}
            >
              {subtitle}
            </Text>

            {recap ? (
              <View
                style={{
                  width: "100%",
                  maxWidth: 460,
                  paddingHorizontal: 4,
                  marginBottom: personalizedMessage ? 16 : 20,
                }}
              >
                {showRecapLabel ? (
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      fontWeight: "500",
                      color: EMBER.colors.textFaint,
                      marginBottom: 5,
                    }}
                  >
                    {t("med_history.complete.recap_label")}
                  </Text>
                ) : null}
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 15,
                    lineHeight: 22,
                    color: EMBER.colors.text,
                  }}
                >
                  {recap}
                </Text>
              </View>
            ) : null}

            {personalizedMessage ? (
              <View
                style={{
                  width: "100%",
                  maxWidth: 460,
                  flexDirection: showInsightLabel ? "row" : "column",
                  alignItems: "flex-start",
                  gap: showInsightLabel ? 10 : 0,
                  paddingHorizontal: 4,
                  marginBottom: 20,
                }}
              >
                {showInsightLabel ? (
                  <Sparkles
                    size={17}
                    color={EMBER.colors.warm}
                    strokeWidth={1.8}
                  />
                ) : null}
                <View style={{ flex: 1 }}>
                  {showInsightLabel ? (
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 12,
                        fontWeight: "500",
                        color: EMBER.colors.textFaint,
                        marginBottom: 4,
                      }}
                    >
                      {t("med_history.complete.insight_label")}
                    </Text>
                  ) : null}
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 14,
                      lineHeight: 21,
                      color: EMBER.colors.textLight,
                    }}
                  >
                    {personalizedMessage}
                  </Text>
                </View>
              </View>
            ) : null}

            {resolvedSecondarySubtitle ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  color: EMBER.colors.textFaint,
                  textAlign: "center",
                  lineHeight: 20,
                  marginBottom: 22,
                }}
              >
                {resolvedSecondarySubtitle}
              </Text>
            ) : null}

            {hasLocalAnswers ? (
              <Text
                className="font-dm-sans"
                accessibilityLiveRegion="polite"
                style={{
                  fontSize: 13,
                  color: EMBER.colors.warm,
                  textAlign: "center",
                  lineHeight: 20,
                  marginBottom: 18,
                }}
              >
                {t("med_history.complete.local_answers_notice")}
              </Text>
            ) : null}

            {children}

            {saveError && !isSubmitting ? (
              <ActionBanner
                variant="important"
                message={saveError}
                actionLabel={t("clinical_data.retry")}
                action="medical_history.save_retry"
                actionIcon={RotateCcw}
                onAction={onFinish}
                style={{ marginBottom: 14 }}
              />
            ) : null}

            <PrimaryButton
              label={
                isSubmitting ? resolvedSubmittingLabel : resolvedButtonLabel
              }
              loading={isSubmitting}
              onPress={onFinish}
              hapticStyle="light"
              trailing={
                !isSubmitting ? (
                  <ArrowRight size={18} color="#FFFFFF" strokeWidth={2} />
                ) : undefined
              }
              style={{ width: "100%", marginTop: 4 }}
              contentStyle={{ minHeight: 54 }}
            />
          </View>
        </ScrollView>
      </View>
    </GlassScreenLayout>
  );
}

function CompletionIllustration() {
  return (
    <View
      accessible
      accessibilityLabel="Section complete"
      style={{
        width: 132,
        height: 132,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 4,
      }}
    >
      <View
        style={{
          width: 112,
          height: 112,
          borderRadius: 56,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(255,255,255,0.52)",
          borderWidth: 1,
          borderColor: "rgba(255,255,255,0.7)",
        }}
      >
        <View
          style={{
            width: 78,
            height: 78,
            borderRadius: 39,
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(184,74,22,0.1)",
          }}
        >
          <ClipboardCheck
            size={44}
            color={EMBER.colors.text}
            strokeWidth={1.6}
          />
        </View>
      </View>
      <View
        style={{
          position: "absolute",
          top: 7,
          right: 13,
          width: 22,
          height: 22,
          borderRadius: 11,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(255,255,255,0.7)",
        }}
      >
        <Check size={13} color={EMBER.colors.warm} strokeWidth={2.5} />
      </View>
    </View>
  );
}

/**
 * Navigate to the next medical history section in order, or back to the index if last.
 * @param navigation - the screen's navigation prop (from inside a nested flow navigator)
 * @param currentSectionKey - key of the current section (e.g. "basicInfo", "infertility")
 */
export function navigateToNextSection(
  navigation: {
    getParent: () => NavigationProp<ParamListBase> | undefined;
    canGoBack: () => boolean;
    goBack: () => void;
    navigate: (route: string) => void;
  },
  currentSectionKey: string,
  patient?: Patient | null,
) {
  const next = getNextSection(currentSectionKey, patient);
  const parent = navigation.getParent();

  if (next && parent) {
    // Replace the current section modal with the next one so that
    // pressing close/back in the new section returns to the medical
    // history index instead of looping back to this complete screen.
    const parentState = parent.getState();
    const routes = parentState.routes;
    parent.dispatch(
      CommonActions.reset({
        ...parentState,
        routes: [...routes.slice(0, -1), { name: next.route }],
        index: routes.length - 1,
      }),
    );
  } else if (parent?.canGoBack()) {
    parent.goBack();
  } else if (navigation.canGoBack()) {
    navigation.goBack();
  } else {
    navigation.navigate("MedicalHistory");
  }
}

/**
 * Get the button label for the next section in order.
 * Accepts the translation function so callers can pass it from `useTranslations()`.
 */
export function getNextSectionButtonLabel(
  t: TranslationFunction,
  currentSectionKey: string,
  patient?: Patient | null,
): string {
  const next = getNextSection(currentSectionKey, patient);
  if (next) {
    const sectionLabel = t(`med_history.complete.next_section.${next.key}`);
    return t("med_history.complete.next_section_prefix", {
      params: { section: sectionLabel },
    });
  }
  return t("med_history.complete.back_to_medical_history");
}

/** @deprecated Use navigateToNextSection instead */
export function navigateBackToMedicalHistory(navigation: {
  getParent: () => NavigationProp<ParamListBase> | undefined;
  canGoBack: () => boolean;
  goBack: () => void;
  navigate: (route: string) => void;
}) {
  const parent = navigation.getParent();
  if (parent?.canGoBack()) {
    parent.goBack();
  } else if (navigation.canGoBack()) {
    navigation.goBack();
  } else {
    navigation.navigate("MedicalHistory");
  }
}

export default BaseCompleteScreen;
